import React, { useState } from "react";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import StatusBadge from "designV2/components/StatusBadge";
import DialogShell from "designV2/components/DialogShell";
import PaymentDetails from "superAdminViews/finance/pay/PaymentDetails";
import "./transactionDetails.css";

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const CopyableField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="transactionDetailsField">
      <span className="transactionDetailsLabel">{label}</span>
      <span className="transactionDetailsValueRow">
        <span className="transactionDetailsMono">{value}</span>
        <IconButton size="small" className="transactionDetailsCopyButton" onClick={handleCopy} title="Copy">
          {copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
        </IconButton>
      </span>
    </div>
  );
};

/**
 * ShopTransactionDetails - a shop-admin-only redesign of the shared
 * superAdminViews/finance/transactions/TransactionDetails.js.
 *
 * That component is also used from several super admin screens
 * (Pay.js, legacyPay.js, superAdminViews/finance/payments/Payments.js),
 * so it was left alone rather than redesigned in place — same reasoning
 * as the dedicated shop-only forms built elsewhere in this redesign. A
 * shop admin never refunds a transaction (every call site here passed
 * hasRefunder={false} or omitted it), so this version drops that whole
 * branch instead of carrying it as dead weight.
 *
 * Content-only — rendered inside a DialogShell by Payments.js and
 * Transactions.js, same pattern as PaymentDetails. Manages its own
 * nested "view payment" dialog for the payments list below, so call
 * sites don't need to wire that up themselves.
 */
const ShopTransactionDetails = ({ transaction }) => {
  const [paymentDetail, setPaymentDetail] = useState(null);

  return (
    <div className="transactionDetailsBody">
      <div className="transactionDetailsTopRow">
        {transaction.status && (
          <StatusBadge
            status={STATUS_TONES[transaction.status] || "neutral"}
            label={transaction.status.replace(/_/g, " ").toLowerCase()}
          />
        )}
        {transaction.reversed && <StatusBadge status="danger" label="Reversed" dot={false} />}
        {typeof transaction.amount === "number" && !Number.isNaN(transaction.amount) && (
          <span className="transactionDetailsAmount">${transaction.amount.toFixed(2)}</span>
        )}
      </div>

      <div className="transactionDetailsGrid">
        <CopyableField label="Transaction ref" value={transaction.transactionId} />
        {transaction.type && (
          <div className="transactionDetailsField">
            <span className="transactionDetailsLabel">Type</span>
            <span className="transactionDetailsValue">{transaction.type.replace(/_/g, " ").toLowerCase()}</span>
          </div>
        )}
        <div className="transactionDetailsField">
          <span className="transactionDetailsLabel">Paid at</span>
          <span className="transactionDetailsValue">
            {transaction.paidAt ? moment(transaction.paidAt).format("MMM D, YYYY hh:mm A") : "Not yet"}
          </span>
        </div>
        <div className="transactionDetailsField">
          <span className="transactionDetailsLabel">Created</span>
          <span className="transactionDetailsValue">{moment(transaction.createdAt).format("MMM D, YYYY hh:mm A")}</span>
        </div>
        <CopyableField label="Payment intent" value={transaction.paymentIntent} />
        <CopyableField label="Reversal ID" value={transaction.reversalId} />
        <CopyableField label="Refund ID" value={transaction.refundId} />
      </div>

      <div className="transactionDetailsField">
        <span className="transactionDetailsLabel">
          Payments ({(transaction.payments || []).length})
        </span>
        <div className="transactionDetailsPayments">
          {(transaction.payments || []).length === 0 ? (
            <span className="transactionDetailsMuted">No payments on this transaction</span>
          ) : (
            transaction.payments.map((payment) => (
              <button
                type="button"
                key={payment._id}
                className="transactionDetailsPaymentRow"
                onClick={() => setPaymentDetail(payment)}
              >
                <span className="transactionDetailsPaymentInfo">
                  <span className="transactionDetailsMono">{payment._id}</span>
                  <span className="transactionDetailsMuted">
                    {payment.createdAt ? moment(payment.createdAt).format("MMM D, hh:mm A") : ""}
                  </span>
                </span>
                {typeof payment.amount === "number" && (
                  <span className="transactionDetailsPaymentAmount">${payment.amount.toFixed(2)}</span>
                )}
                <ChevronRightIcon fontSize="small" />
              </button>
            ))
          )}
        </div>
      </div>

      <DialogShell open={Boolean(paymentDetail)} onClose={() => setPaymentDetail(null)} title="Payment details">
        {paymentDetail && <PaymentDetails close={() => setPaymentDetail(null)} payment={paymentDetail} />}
      </DialogShell>
    </div>
  );
};

export default ShopTransactionDetails;
